import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, HostListener, Inject, PLATFORM_ID, ViewChildren, QueryList } from '@angular/core';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { DynamicTranslationService, RestfulApiService, OrganizationProjectService, CurrencyService, CurrencyPreference, DonorGivingService, GivingCardData, AnalyticsService, getCurrencySign } from '@gs-2.0/core';
import { FeaturedCardComponent } from '../featured-card/featured-card.component';

export interface Card {
  imageUrl: string | null;
  causeName: string | null;
  name: string | null;
  description: string | null;
  uuid: string;
  giveLink: string;
  amountRaisedToDate?: number | null;
  languageCode?: string | null;
  organisationName?: string | null;
  principalAmountCurrencySymbol?: string | null;
  totalProjectBudget?: number | null;
  currencySymbol?: string | null;
}

export interface FeaturedTab {
  label: string;
  type: 'charity' | 'project';
  cards: Card[];
}

@Component({
  selector: 'app-features-section',
  standalone: false,
  templateUrl: './features-section.component.html',
  styleUrls: ['./features-section.component.scss']
})
export class FeaturesSectionComponent implements OnInit, OnDestroy, OnChanges {
  subscriptions = new Subscription()

  @ViewChildren(FeaturedCardComponent) featuredCards!: QueryList<FeaturedCardComponent>;

  @Input() eyebrowIcon: string = '';
  @Input() eyebrowIconColor: string = '#96080B';
  @Input() eyebrowText: string = '';
  @Input() headline: string = 'Impact in Action';
  @Input() tabs: FeaturedTab[] = [];
  @Input() onButtonClick?: () => void;
  @Input() carouselIconColor: string = '#96080B';

  // Store original tabs for currency conversion
  private originalTabs: FeaturedTab[] = [];

  // Selected currency
  selectedCurrency: CurrencyPreference | null = null;

  selectedTabIndex = 0;
  currentCardIndices: number[] = [];
  cardsPerView = 4;
  maxCards = 20
  showCarouselBtns: boolean[] = [];
  showCarouselIndicators: boolean[] = [];
  buttonLabel = 'home.cards.tab.view-charities'
  isMobile = false

  // Swipe properties
  private touchStartX = 0;
  private touchStartY = 0;
  private touchEndX = 0;
  private touchEndY = 0;
  private isDragging = false;
  private currentTranslateX = 0;
  private readonly SWIPE_THRESHOLD = 100; // Minimum distance to trigger swipe
  private readonly SWIPE_VELOCITY_THRESHOLD = 0.5; // Minimum velocity
  private touchStartTime = 0;
  private readonly STACK_SIZE = 3; // Number of cards in stack (current, next, previous)
  currentSwipingCardIndex = -1; // Track which card is being swiped
  isSwipingAway = false; // Track if card is being animated away

  /**
   * Subject for component destruction
   */
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private apiService: RestfulApiService,
    private organizationProjectService: OrganizationProjectService,
    private translationService: DynamicTranslationService,
    private currencyService: CurrencyService,
    private donorGivingService: DonorGivingService,
    private analyticsService: AnalyticsService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.checkScreenSize();
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }

  checkScreenSize() {
    if (typeof window !== 'undefined') {
      this.isMobile = window.innerWidth <= 767;
      // Show 1 card at a time on mobile for swipe functionality
      this.cardsPerView = this.isMobile ? 1 : 4;
      // Recalculate carousel indicators when screen size changes
      this.checkCarouselIndicators();
    }
  }

  ngOnInit(): void {
    this.updateButtonLabel(this.selectedTabIndex);
    this.checkCarouselIndicators()

    // Subscribe to currency changes
    this.subscriptions.add(
      this.currencyService.currentCurrency$.subscribe((currency) => {
        if (currency) {
          this.selectedCurrency = currency;
          // Re-apply currency conversion when currency changes
          this.applyCurrentCurrencyToProjectTabs();
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabs']) {
      const currentTabs = (changes['tabs'].currentValue as FeaturedTab[]) ?? [];
      // Reset per-tab indices
      this.currentCardIndices = new Array(currentTabs.length).fill(0);
      this.checkCarouselIndicators();
      this.updateButtonLabel(this.selectedTabIndex);

      // Deep copy originals for currency conversion
      this.originalTabs = currentTabs.map(tab => ({
        ...tab,
        cards: tab.cards.map(card => ({ ...card }))
      }));
      this.applyCurrentCurrencyToProjectTabs();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // Complete the destroy$ subject
    this.destroy$.next();
    this.destroy$.complete();
  }

  tabChanged(index: number) {
    this.updateButtonLabel(index);
  }

  private updateButtonLabel(tabIndex: number): void {
    const tab = this.tabs[tabIndex];
    this.buttonLabel = tab?.type === 'project'
      ? 'home.cards.tab.view-projects'
      : 'home.cards.tab.view-charities';
  }

  checkCarouselIndicators() {
    this.tabs.forEach((tab, i) => {
      if (tab.cards.length > this.cardsPerView) {
        this.showCarouselBtns[i] = true;
        this.showCarouselIndicators[i] = true;
        if (tab.cards.length > this.maxCards) {
          tab.cards.splice(20);
        }
      } else {
        this.showCarouselBtns[i] = false;
        this.showCarouselIndicators[i] = false;
      }
    });
  }

  get visibleCards(): Card[] {
    const tab = this.tabs[this.selectedTabIndex];
    const cards = tab?.cards ?? [];
    const index = this.currentCardIndices[this.selectedTabIndex] ?? 0;

    // On mobile, show stack with current, next, and previous cards
    if (this.isMobile) {
      const stackCards: Card[] = [];

      // Current card (top of stack)
      stackCards.push(cards[index]);

      // Next card (for left swipe)
      const nextIndex = (index + 1) % cards.length;
      if (cards[nextIndex]) {
        stackCards.push(cards[nextIndex]);
      }

      // Previous card (for right swipe)
      const prevIndex = index === 0 ? cards.length - 1 : index - 1;
      if (cards[prevIndex] && prevIndex !== index) {
        stackCards.push(cards[prevIndex]);
      }

      return stackCards;
    }

    return cards.slice(index, index + this.cardsPerView);
  }

  get totalPages(): number {
    const cards = this.tabs[this.selectedTabIndex]?.cards ?? [];
    return Math.ceil(cards.length / this.cardsPerView);
  }

  get currentPage(): number {
    const index = this.currentCardIndices[this.selectedTabIndex] ?? 0;
    return Math.floor(index / this.cardsPerView) + 1;
  }

  get totalCards(): number {
    return this.tabs[this.selectedTabIndex]?.cards?.length ?? 0;
  }

  get hasSectionData(): boolean {
    return this.tabs.some(tab => tab.cards.length > 0);
  }

  previousCards(): void {
    const cards = this.tabs[this.selectedTabIndex]?.cards ?? [];
    const currentIndex = this.currentCardIndices[this.selectedTabIndex] ?? 0;

    let newIndex: number;
    if (currentIndex > 0) {
      newIndex = Math.max(0, currentIndex - this.cardsPerView);
    } else {
      if (cards.length % this.cardsPerView) {
        newIndex = this.cardsPerView * Math.floor(cards.length / this.cardsPerView);
      } else {
        newIndex = cards.length - this.cardsPerView;
      }
    }

    this.currentCardIndices[this.selectedTabIndex] = newIndex;
  }

  nextCards(): void {
    const cards = this.tabs[this.selectedTabIndex]?.cards ?? [];
    const currentIndex = this.currentCardIndices[this.selectedTabIndex] ?? 0;

    let newIndex: number;
    if (currentIndex + this.cardsPerView < cards.length) {
      newIndex = currentIndex + this.cardsPerView;
    } else {
      newIndex = 0;
    }

    this.currentCardIndices[this.selectedTabIndex] = newIndex;
  }

  goToPage(pageIndex: number): void {
    this.currentCardIndices[this.selectedTabIndex] = pageIndex * this.cardsPerView;
  }

  getViewAllLink(): string {
    const tab = this.tabs[this.selectedTabIndex];
    if (tab?.type === 'charity') return '/charities';
    if (tab?.type === 'project') return '/projects';
    return '';
  }

  handleButtonClick(): void {
    if (this.onButtonClick) {
      this.onButtonClick();
    }
  }

  /**
   * Handles the Give button click event for a card
   * @param card - The card data
   */
  handleGiveClick(card: Card): void {
    const tab = this.tabs[this.selectedTabIndex];
    const entityType = tab?.type === 'project' ? 'project' : 'charity';

    const givingCardData: GivingCardData = {
      uuid: card.uuid,
      languageCode: card.languageCode,
      entityType: entityType
    };

    this.analyticsService.trackGiveButtonClick(card.name ?? undefined);
    this.donorGivingService.handleGiveClick(givingCardData, this.destroy$);
  }

  handleLearnMoreClick(card: Card): void {
    const tab = this.tabs[this.selectedTabIndex];
    const languageCode = card.languageCode || this.translationService.getIso639Code();

    if (tab?.type === 'charity') {
      this.router.navigate(['/charity', card.uuid, languageCode, 'summary']);
      return;
    }

    if (tab?.type === 'project') {
      this.router.navigate(['/project', card.uuid, languageCode, 'summary']);
      return;
    }
  }

  getGiveUrl(): string | undefined {
    // Only provide URL for desktop view (links), mobile uses buttons
    if (!this.isMobile && isPlatformBrowser(this.platformId)) {
      return this.apiService.prependHttp(`${this.apiService.getBaseUrl()}/app/portal/public/donate`);
    }
    return undefined;
  }

  getLearnMoreUrl(card: Card): string | undefined {
    // Only provide URL for desktop view (links), mobile uses buttons
    if (!this.isMobile && isPlatformBrowser(this.platformId)) {
      const tab = this.tabs[this.selectedTabIndex];
      const languageCode = card.languageCode || this.translationService.getIso639Code();

      if (tab?.type === 'charity') {
        return `/charity/${card.uuid}/${languageCode}/summary`;
      }

      if (tab?.type === 'project') {
        return `/project/${card.uuid}/${languageCode}/summary`;
      }
    }
    return undefined;
  }

  // Get container styles
  getContainerStyle(): any {
    return {};
  }

  // Get card stack styles for each card position
  getCardStackStyle(cardIndex: number): any {
    if (!this.isMobile) return {};

    const isTopCard = cardIndex === 0;
    const isNextCard = cardIndex === 1; // Card for left swipe
    const isPrevCard = cardIndex === 2; // Card for right swipe

    // Create visible stack effect to indicate swipability
    // Cards are stacked with their bottoms peeking out
    let scale = 0.96;
    let translateY = 35; // Offset to show bottom portion
    let translateX = 0;
    let zIndex = 1;
    let opacity = 0.95;

    // Top card always has full size and highest z-index
    if (isTopCard) {
      scale = 1;
      translateY = 0;
      translateX = 0;
      zIndex = 10;
      opacity = 1;
    } else if (isNextCard) {
      // Next card behind - bottom portion visible
      scale = 0.96;
      translateY = 35; // Push down to show bottom
      translateX = 0;
      zIndex = 1;
      opacity = 0.95;
    } else if (isPrevCard) {
      // Previous card - same position as next but hidden by default
      scale = 0.96;
      translateY = 35;
      translateX = 0;
      zIndex = 1;
      opacity = 0.95;
    }

    // If card is being swiped away, keep it off screen
    if (isTopCard && this.isSwipingAway) {
      const rotation = (this.currentTranslateX / window.innerWidth) * 20;
      return {
        transform: `translateX(${this.currentTranslateX}px) translateY(0) scale(1) rotate(${rotation}deg)`,
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
        zIndex: 100, // Keep swiping card on top
        opacity: 0
      };
    }

    // Keep background card visible when top card is swiping away
    if (!isTopCard && this.isSwipingAway) {
      const isSwipingLeft = this.currentTranslateX < 0;
      const isSwipingRight = this.currentTranslateX > 0;

      // Show only the card in the direction of swipe
      const shouldShow = (isNextCard && isSwipingLeft) || (isPrevCard && isSwipingRight);

      return {
        transform: `translateX(${translateX}px) translateY(${translateY}px) scale(${scale})`,
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease',
        zIndex: 1,
        opacity: shouldShow ? 1 : 0
      };
    }

    // If this is the card being swiped, apply swipe transform
    if (isTopCard && this.currentSwipingCardIndex === 0 && this.isDragging) {
      const rotation = (this.currentTranslateX / window.innerWidth) * 15;
      opacity = 1 - Math.abs(this.currentTranslateX) / (window.innerWidth * 0.8);
      return {
        transform: `translateX(${this.currentTranslateX}px) translateY(0) scale(1) rotate(${rotation}deg)`,
        transition: 'none',
        zIndex: 10,
        opacity: opacity
      };
    }

    // Show only one background card at a time based on swipe direction
    let opacityTransition = 'opacity 0.2s ease-out';

    if (!isTopCard) {
      // When dragging, show only the relevant card
      if (this.isDragging) {
        const showNextCard = this.currentTranslateX < 0; // Swiping left
        const showPrevCard = this.currentTranslateX > 0; // Swiping right

        // Hide the card that's not relevant to current swipe
        if ((isNextCard && !showNextCard) || (isPrevCard && !showPrevCard)) {
          opacity = 0;
        }
      } else {
        // When not dragging, show next card by default, hide previous
        if (isPrevCard) {
          opacity = 0;
        }
      }
    }

    return {
      transform: `translateX(${translateX}px) translateY(${translateY}px) scale(${scale})`,
      transition: `transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), ${opacityTransition}`,
      zIndex: zIndex,
      opacity: opacity
    };
  }

  // Swipe handling methods
  onTouchStart(event: TouchEvent, cardIndex: number): void {
    if (!this.isMobile || cardIndex !== 0) return; // Only top card is swipeable

    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.touchStartTime = Date.now();
    this.isDragging = false;
    this.currentSwipingCardIndex = cardIndex;
  }

  onTouchMove(event: TouchEvent, cardIndex: number): void {
    if (!this.isMobile || cardIndex !== 0 || this.currentSwipingCardIndex !== 0) return;

    const currentX = event.touches[0].clientX;
    const currentY = event.touches[0].clientY;
    const deltaX = currentX - this.touchStartX;
    const deltaY = currentY - this.touchStartY;

    // Only start dragging if horizontal movement is greater than vertical
    if (!this.isDragging && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      this.isDragging = true;
    }

    if (this.isDragging) {
      // Prevent default scrolling behavior
      event.preventDefault();

      // No resistance for stack - always allow swiping
      this.currentTranslateX = deltaX;
    }
  }

  onTouchEnd(event: TouchEvent, cardIndex: number): void {
    if (!this.isMobile || cardIndex !== 0 || !this.isDragging) {
      this.resetSwipeState();
      return;
    }

    this.touchEndX = event.changedTouches[0].clientX;
    this.touchEndY = event.changedTouches[0].clientY;

    const deltaX = this.touchEndX - this.touchStartX;
    const deltaTime = Date.now() - this.touchStartTime;
    const velocity = Math.abs(deltaX) / deltaTime;

    const shouldSwipe =
      Math.abs(deltaX) > this.SWIPE_THRESHOLD ||
      velocity > this.SWIPE_VELOCITY_THRESHOLD;

    if (shouldSwipe) {
      this.handleSwipe(deltaX);
    } else {
      this.snapBack();
    }

    this.isDragging = false;
  }

  private handleSwipe(deltaX: number): void {
    // Mark the card as being animated off screen
    const direction = deltaX > 0 ? 1 : -1;
    this.currentTranslateX = direction * window.innerWidth * 1.5;
    this.isSwipingAway = true;
    this.isDragging = false;

    // Wait for swipe animation to complete, then immediately change cards
    setTimeout(() => {
      // Swipe right (positive deltaX) = previous card
      // Swipe left (negative deltaX) = next card
      if (deltaX > 0) {
        this.previousCards();
      } else {
        this.nextCards();
      }

      // Reset swipe state immediately so new cards appear in correct position
      this.currentTranslateX = 0;
      this.isDragging = false;
      this.currentSwipingCardIndex = -1;
      this.isSwipingAway = false;
    }, 300);
  }

  private snapBack(): void {
    this.currentTranslateX = 0;
    this.isDragging = false;

    setTimeout(() => {
      this.resetSwipeState();
    }, 300);
  }

  private resetSwipeState(): void {
    this.currentTranslateX = 0;
    this.isDragging = false;
    this.currentSwipingCardIndex = -1;
    this.isSwipingAway = false;
  }

  /**
   * Convert amount using the currency service
   */
  private convertAmount(amount: number): number {
    return this.currencyService.convertAmount(amount);
  }

  /**
   * Get currency symbol using the currency service
   */
  private getCurrencySymbol(currencyName: string): string {
    return this.currencyService.getCurrencySymbol(currencyName);
  }

  /**
   * Re-apply currency conversion to all project-type tabs
   */
  private applyCurrentCurrencyToProjectTabs(): void {
    if (!this.originalTabs.length) {
      return;
    }

    this.tabs = this.originalTabs.map(tab => {
      if (tab.type !== 'project') {
        return { ...tab, cards: [...tab.cards] };
      }

      return {
        ...tab,
        cards: tab.cards.map(project => {
          const converted = { ...project };
          if (converted.amountRaisedToDate != null && converted.totalProjectBudget != null) {
            converted.amountRaisedToDate = this.convertAmount(converted.amountRaisedToDate);
            converted.totalProjectBudget = this.convertAmount(converted.totalProjectBudget);
            converted.currencySymbol = this.selectedCurrency
              ? this.getCurrencySymbol(this.selectedCurrency.name)
              : getCurrencySign(converted.principalAmountCurrencySymbol || '', '$');
          }
          return converted;
        })
      };
    });
  }

}
